import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_OPTIONS = [
  { value: 'welcome_call', label: 'Welcome Call', color: '#3b82f6' },
  { value: 'checklist_shared', label: 'Checklist Shared', color: '#8b5cf6' },
  { value: 'documents_received', label: 'Documents Received', color: '#06b6d4' },
  { value: 'processing', label: 'Processing', color: '#f59e0b' },
  { value: 'visa_approved', label: 'Visa Approved', color: '#22c55e' },
  { value: 'visa_rejected', label: 'Visa Rejected', color: '#ef4444' },
  { value: 'delivered', label: 'Delivered', color: '#10b981' }
];

export default function ClientManagement() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [filters, setFilters] = useState({
    stage: '',
    visa_status: ''
  });
  const [serviceStats, setServiceStats] = useState(null);
  const [documentName, setDocumentName] = useState('');

  useEffect(() => {
    fetchClients();
    fetchServiceStats();
  }, [filters]);

  const fetchClients = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.stage) queryParams.append('stage', filters.stage);
      if (filters.visa_status) queryParams.append('visa_status', filters.visa_status);

      const response = await fetch(
        `${BACKEND_URL}/api/clients?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/stats/service-delivery`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setServiceStats(data);
      }
    } catch (error) {
      console.error('Error fetching service stats:', error);
    }
  };

  const handleViewDetails = async (client) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${client.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedClient(data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
    }
  };

  const handleUpdateStage = async (clientId, newStage) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({ stage: newStage })
      });

      if (response.ok) {
        fetchClients();
        fetchServiceStats();
        if (selectedClient && selectedClient.id === clientId) {
          const updatedClient = await response.json();
          setSelectedClient(updatedClient);
        }
      }
    } catch (error) {
      console.error('Error updating stage:', error);
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clients/${selectedClient.id}/documents?document_name=${documentName}&status=pending`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (response.ok) {
        setShowDocumentModal(false);
        setDocumentName('');
        // Refresh client details
        const clientResponse = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
        });
        if (clientResponse.ok) {
          const updatedClient = await clientResponse.json();
          setSelectedClient(updatedClient);
        }
      }
    } catch (error) {
      console.error('Error adding document:', error);
    }
  };

  const handleUpdateDocumentStatus = async (documentId, newStatus) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clients/${selectedClient.id}/documents/${documentId}?status=${newStatus}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (response.ok) {
        // Refresh client details
        const clientResponse = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
        });
        if (clientResponse.ok) {
          const updatedClient = await clientResponse.json();
          setSelectedClient(updatedClient);
        }
      }
    } catch (error) {
      console.error('Error updating document status:', error);
    }
  };

  const getStageColor = (stage) => {
    const stageOption = STAGE_OPTIONS.find(s => s.value === stage);
    return stageOption ? stageOption.color : '#94a3b8';
  };

  return (
    <div className="client-management">
      <div className="page-header">
        <h1>Client Management</h1>
      </div>

      {/* Service Delivery Statistics */}
      {serviceStats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Clients</h3>
            <p className="stat-value">{serviceStats.total_clients}</p>
          </div>
          <div className="stat-card">
            <h3>Visa Approved</h3>
            <p className="stat-value success">{serviceStats.approved}</p>
          </div>
          <div className="stat-card">
            <h3>Visa Rejected</h3>
            <p className="stat-value error">{serviceStats.rejected}</p>
          </div>
          <div className="stat-card">
            <h3>Approval Rate</h3>
            <p className="stat-value">{serviceStats.approval_rate.toFixed(1)}%</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <select
          value={filters.stage}
          onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
          className="filter-select"
        >
          <option value="">All Stages</option>
          {STAGE_OPTIONS.map(stage => (
            <option key={stage.value} value={stage.value}>{stage.label}</option>
          ))}
        </select>

        <select
          value={filters.visa_status}
          onChange={(e) => setFilters({ ...filters, visa_status: e.target.value })}
          className="filter-select"
        >
          <option value="">All Visa Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Clients Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Service</th>
              <th>Stage</th>
              <th>Visa Status</th>
              <th>Process Consultant</th>
              <th>Visa Consultant</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="text-center">Loading...</td></tr>
            ) : clients.length === 0 ? (
              <tr><td colSpan="8" className="text-center">No clients found</td></tr>
            ) : (
              clients.map(client => (
                <tr key={client.id}>
                  <td data-label="Name">{client.name}</td>
                  <td data-label="Contact">
                    <div>{client.email}</div>
                    <div className="text-muted">{client.phone}</div>
                  </td>
                  <td data-label="Service">
                    <div>{client.service_type || 'N/A'}</div>
                    <div className="text-muted">{client.destination_country || 'N/A'}</div>
                  </td>
                  <td data-label="Stage">
                    <span className="badge" style={{ backgroundColor: getStageColor(client.stage), color: 'white' }}>
                      {STAGE_OPTIONS.find(s => s.value === client.stage)?.label || client.stage}
                    </span>
                  </td>
                  <td data-label="Visa Status">
                    <span className={`badge badge-${client.visa_status || 'pending'}`}>
                      {client.visa_status || 'pending'}
                    </span>
                  </td>
                  <td data-label="Process Consultant">{client.assigned_to}</td>
                  <td data-label="Visa Consultant">{client.sales_consultant}</td>
                  <td data-label="Actions">
                    <button
                      className="btn-small"
                      onClick={() => handleViewDetails(client)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Client Detail Modal */}
      {showDetailModal && selectedClient && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <h2>Client Details: {selectedClient.name}</h2>
            
            <div className="client-details">
              <div className="detail-section">
                <h3>Contact Information</h3>
                <p><strong>Email:</strong> {selectedClient.email}</p>
                <p><strong>Phone:</strong> {selectedClient.phone}</p>
              </div>

              <div className="detail-section">
                <h3>Service Details</h3>
                <p><strong>Service Type:</strong> {selectedClient.service_type || 'N/A'}</p>
                <p><strong>Destination:</strong> {selectedClient.destination_country || 'N/A'}</p>
                <p><strong>Number of Applicants:</strong> {selectedClient.num_applicants || 1}</p>
              </div>

              <div className="detail-section">
                <h3>Stage Management</h3>
                <p><strong>Current Stage:</strong> {selectedClient.stage}</p>
                <select
                  onChange={(e) => handleUpdateStage(selectedClient.id, e.target.value)}
                  className="stage-selector"
                  value={selectedClient.stage}
                >
                  {STAGE_OPTIONS.map(stage => (
                    <option key={stage.value} value={stage.value}>{stage.label}</option>
                  ))}
                </select>
                <p><strong>Visa Status:</strong> {selectedClient.visa_status || 'pending'}</p>
              </div>

              <div className="detail-section">
                <h3>Consultants</h3>
                <p><strong>Process Consultant:</strong> {selectedClient.assigned_to}</p>
                <p><strong>Visa Consultant:</strong> {selectedClient.sales_consultant}</p>
              </div>

              <div className="detail-section">
                <h3>Document Checklist</h3>
                <button className="btn-primary mb-2" onClick={() => setShowDocumentModal(true)}>
                  + Add Document
                </button>
                {selectedClient.document_checklist && selectedClient.document_checklist.length > 0 ? (
                  <div className="document-list">
                    {selectedClient.document_checklist.map((doc) => (
                      <div key={doc.id} className="document-item">
                        <div>
                          <strong>{doc.name}</strong>
                          <span className={`badge badge-${doc.status}`} style={{ marginLeft: '10px' }}>
                            {doc.status}
                          </span>
                        </div>
                        <select
                          value={doc.status}
                          onChange={(e) => handleUpdateDocumentStatus(doc.id, e.target.value)}
                          className="status-select"
                        >
                          <option value="pending">Pending</option>
                          <option value="requested">Requested</option>
                          <option value="uploaded">Uploaded</option>
                          <option value="verified">Verified</option>
                        </select>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No documents added yet</p>
                )}
              </div>

              {selectedClient.stage_history && selectedClient.stage_history.length > 0 && (
                <div className="detail-section">
                  <h3>Stage History</h3>
                  <div className="stage-history">
                    {selectedClient.stage_history.map((history, index) => (
                      <div key={index} className="history-item">
                        <strong>{history.stage}</strong> - {new Date(history.changed_at).toLocaleString()}
                        <br />
                        <span className="text-muted">By: {history.changed_by}</span>
                        {history.notes && <p>{history.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Document Modal */}
      {showDocumentModal && (
        <div className="modal-overlay" onClick={() => setShowDocumentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Document to Checklist</h2>
            <form onSubmit={handleAddDocument}>
              <div className="form-group">
                <label>Document Name *</label>
                <input
                  type="text"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  placeholder="e.g., Passport Copy, Bank Statement"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowDocumentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Add Document</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
